import React, { useState, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  TextField,
  Typography,
  IconButton,
  Alert,
  Paper,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useTranslation } from '../../../app/LanguageContext';
import { primarySoftSurfaceBg, subtleSurfaceBg } from '../../../theme/surfaceSx';
import {
  MAX_OPTIONS,
  VARIANT_CONFIRM_AT,
  VARIANT_HARD_MAX,
  VARIANT_WARN_AT,
  cloneOptionsForEditor,
  computeValueRenames,
  countVariantsFromOptions,
} from './productVariantUtils';

function SortableValueRow({ value, optionIndex, valueIndex, onChange, onRemove }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: `value-${optionIndex}-${valueIndex}`,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Paper
      ref={setNodeRef}
      style={style}
      variant="outlined"
      sx={{
        px: 1.25,
        py: 0.5,
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        bgcolor: primarySoftSurfaceBg,
      }}
    >
      <IconButton
        size="small"
        {...attributes}
        {...listeners}
        sx={{ cursor: 'grab', '&:active': { cursor: 'grabbing' }, p: 0.25 }}
      >
        <DragIndicatorIcon sx={{ fontSize: 14, color: 'primary.main' }} />
      </IconButton>
      <TextField
        value={value}
        onChange={(e) => onChange(valueIndex, e.target.value)}
        size="small"
        variant="standard"
        fullWidth
        sx={{ flex: 1, '& .MuiInput-root': { fontSize: '0.875rem' } }}
        onPointerDown={(e) => e.stopPropagation()}
      />
      <IconButton size="small" onClick={() => onRemove(valueIndex)} sx={{ p: 0.25 }}>
        <CloseIcon sx={{ fontSize: 14 }} />
      </IconButton>
    </Paper>
  );
}

function SortableOptionBlock({ option, index, onChange, onRemove }) {
  const { translate } = useTranslation();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: `option-${index}`,
  });
  const valueSensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const handleValueKeyDown = (e) => {
    if (e.key === 'Enter' && e.target.value.trim()) {
      e.preventDefault();
      const val = e.target.value.trim();
      if (!(option.values || []).includes(val)) {
        onChange(index, { values: [...(option.values || []), val] });
      }
      e.target.value = '';
    }
  };

  const updateValue = (vi, text) => {
    const next = [...(option.values || [])];
    next[vi] = text;
    onChange(index, { values: next });
  };

  const removeValue = (vi) => {
    const next = [...(option.values || [])];
    next.splice(vi, 1);
    onChange(index, { values: next });
  };

  const handleValueDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const parseIndex = (id) => Number(String(id).replace(`value-${index}-`, ''));
    const oldIndex = parseIndex(active.id);
    const newIndex = parseIndex(over.id);
    const next = arrayMove([...(option.values || [])], oldIndex, newIndex);
    onChange(index, { values: next });
  };

  const valueIds = (option.values || []).map((_, vi) => `value-${index}-${vi}`);

  return (
    <Paper ref={setNodeRef} style={style} variant="outlined" sx={{ p: 2, mb: 1.5, bgcolor: subtleSurfaceBg }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <IconButton size="small" {...attributes} {...listeners} sx={{ cursor: 'grab' }}>
          <DragIndicatorIcon fontSize="small" />
        </IconButton>
        <Typography variant="subtitle2">
          {`${translate('products.options')} ${index + 1}`}
        </Typography>
        <Box sx={{ flex: 1 }} />
        <IconButton size="small" color="error" onClick={() => onRemove(index)}>
          <DeleteOutlineIcon fontSize="small" />
        </IconButton>
      </Box>
      <TextField
        fullWidth
        size="small"
        label={translate('products.option_name')}
        value={option.name}
        onChange={(e) => onChange(index, { name: e.target.value })}
        sx={{ mb: 1.5 }}
      />
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.75 }}>
        {translate('products.option_values')}
      </Typography>
      <DndContext sensors={valueSensors} collisionDetection={closestCenter} onDragEnd={handleValueDragEnd}>
        <SortableContext items={valueIds} strategy={verticalListSortingStrategy}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mb: 1 }}>
            {(option.values || []).map((val, vi) => (
              <SortableValueRow
                key={`value-${index}-${vi}`}
                value={val}
                optionIndex={index}
                valueIndex={vi}
                onChange={updateValue}
                onRemove={removeValue}
              />
            ))}
          </Box>
        </SortableContext>
      </DndContext>
      <TextField
        fullWidth
        size="small"
        placeholder={translate('products.option_value_add_placeholder')}
        onKeyDown={handleValueKeyDown}
      />
    </Paper>
  );
}

export default function VariantOptionsDialog({ open, initialOptions = [], onClose, onSave, saving = false }) {
  const { translate } = useTranslation();
  const [draft, setDraft] = useState([]);
  const [localError, setLocalError] = useState('');

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  React.useEffect(() => {
    if (open) {
      setDraft(cloneOptionsForEditor(initialOptions));
      setLocalError('');
    }
  }, [open, initialOptions]);

  const { count, parts } = useMemo(() => countVariantsFromOptions(draft), [draft]);

  const previewSeverity =
    count > VARIANT_HARD_MAX ? 'error' : count >= VARIANT_WARN_AT ? 'warning' : 'success';

  const handleOptionChange = (index, patch) => {
    setDraft((prev) => prev.map((opt, i) => (i === index ? { ...opt, ...patch } : opt)));
  };

  const handleOptionRemove = (index) => {
    setDraft((prev) => prev.filter((_, i) => i !== index));
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = Number(String(active.id).replace('option-', ''));
    const newIndex = Number(String(over.id).replace('option-', ''));
    setDraft((prev) => arrayMove(prev, oldIndex, newIndex));
  };

  const handleAddOption = () => {
    if (draft.length >= MAX_OPTIONS) return;
    setDraft((prev) => [...prev, { name: '', key: '', values: [] }]);
  };

  const handleSave = () => {
    setLocalError('');
    const validOptions = draft
      .map((opt) => ({
        ...opt,
        name: String(opt.name ?? '').trim(),
        values: (opt.values || []).map((v) => String(v).trim()).filter(Boolean),
      }))
      .filter((opt) => opt.name && opt.values.length > 0);

    if (validOptions.length === 0) {
      setLocalError(translate('products.options_required'));
      return;
    }

    const hasEmptySlot = draft.some(
      (opt) =>
        String(opt.name ?? '').trim() &&
        (opt.values || []).some((v) => !String(v ?? '').trim()),
    );
    if (hasEmptySlot) {
      setLocalError(translate('products.options_required'));
      return;
    }

    const duplicateOption = validOptions.find((opt) => {
      const seen = new Set();
      return (opt.values || []).some((val) => {
        if (seen.has(val)) return true;
        seen.add(val);
        return false;
      });
    });
    if (duplicateOption) {
      setLocalError(
        translate('products.option_value_duplicate').replace('{option}', duplicateOption.name),
      );
      return;
    }

    if (count > VARIANT_HARD_MAX) return;
    if (count >= VARIANT_CONFIRM_AT) {
      const ok = window.confirm(
        translate('products.options_save_confirm').replace('{count}', String(count)),
      );
      if (!ok) return;
    } else if (count >= VARIANT_WARN_AT) {
      const ok = window.confirm(
        translate('products.options_save_warn').replace('{count}', String(count)),
      );
      if (!ok) return;
    }
    const valueRenames = computeValueRenames(initialOptions, validOptions);
    onSave?.(validOptions, valueRenames);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        {translate('products.options')}
        <IconButton onClick={onClose} aria-label={translate('common.close')}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {localError ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {localError}
          </Alert>
        ) : null}
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext
            items={draft.map((_, i) => `option-${i}`)}
            strategy={verticalListSortingStrategy}
          >
            {draft.map((opt, index) => (
              <SortableOptionBlock
                key={`opt-${index}`}
                option={opt}
                index={index}
                onChange={handleOptionChange}
                onRemove={handleOptionRemove}
              />
            ))}
          </SortableContext>
        </DndContext>
        {draft.length >= MAX_OPTIONS && (
          <Alert severity="warning" sx={{ mb: 1 }}>
            {translate('products.options_max_hint')}
          </Alert>
        )}
        <Alert severity={previewSeverity} sx={{ mt: 1 }}>
          {count > VARIANT_HARD_MAX
            ? translate('products.options_preview_blocked').replace('{count}', String(count)).replace('{max}', String(VARIANT_HARD_MAX))
            : translate('products.options_preview').replace('{count}', String(count))}
          {parts.length > 0 && (
            <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
              {`${parts.join(' × ')} = ${count}`}
            </Typography>
          )}
        </Alert>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
          {translate('products.reorder_options_hint')}
        </Typography>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between', px: 2, py: 1.5 }}>
        <Button onClick={handleAddOption} disabled={draft.length >= MAX_OPTIONS}>
          {translate('products.add_option')}
        </Button>
        <Box>
          <Button onClick={onClose} sx={{ mr: 1 }}>
            {translate('common.cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || count > VARIANT_HARD_MAX}
          >
            {saving ? translate('common.saving') : translate('products.options_save')}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}
